"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { RefreshCw, TrendingDown, TrendingUp } from "lucide-react";
import type { CoinGlassLiquidations, HistoricalLiquidationHeatmap, LiquidationHeatmap, LiquidationSummary } from "@/lib/types";
import { EmptyState } from "./module-panels";
import { Panel } from "./ui";
import { DATA_REFRESHED_EVENT } from "@/lib/data-refresh";

type LiquidationPeriod = "24h" | "7d" | "30d" | "90d" | "365d";

const LIQUIDATION_PERIODS: Array<{ value: LiquidationPeriod; label: string; heatmapRange: LiquidationHeatmap["range"]; priceInterval: string; priceLimit: number }> = [
  { value: "24h", label: "24h", heatmapRange: "24h", priceInterval: "30m", priceLimit: 48 },
  { value: "7d", label: "7d", heatmapRange: "7d", priceInterval: "4h", priceLimit: 42 },
  { value: "30d", label: "30d", heatmapRange: "30d", priceInterval: "4h", priceLimit: 180 },
  { value: "90d", label: "90d", heatmapRange: "90d", priceInterval: "12h", priceLimit: 180 },
  { value: "365d", label: "365d", heatmapRange: "1y", priceInterval: "1d", priceLimit: 365 }
];

const HEATMAP_RANGE_TITLES: Partial<Record<LiquidationHeatmap["range"], string>> = {
  "24h": "24-hour",
  "7d": "7-day",
  "30d": "30-day",
  "90d": "90-day",
  "1y": "365-day"
};

type KlineBar = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
};

type KlineResponse = { bars?: KlineBar[] };

function websiteUsd(value: number) {
  const absolute = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (absolute >= 1_000_000_000) return `${sign}$${(absolute / 1_000_000_000).toFixed(2)}B`;
  if (absolute >= 1_000_000) return `${sign}$${(absolute / 1_000_000).toFixed(2)}M`;
  if (absolute >= 1_000) return `${sign}$${(absolute / 1_000).toFixed(1)}K`;
  return `${sign}$${absolute.toLocaleString("en", { maximumFractionDigits: 0 })}`;
}

function formatUtcTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC"
  }).format(date).toUpperCase();
}

function dominantLabel(summary: LiquidationSummary) {
  if (summary.dominant_side === "balanced") return "Balanced liquidations";
  return `${summary.dominant_side === "long" ? "Longs" : "Shorts"} ${Math.round(summary.dominant_share * 100)}%`;
}

function LiquidationCard({ summary }: { summary: LiquidationSummary }) {
  const longDominant = summary.dominant_side === "long";
  const tone = summary.dominant_side === "balanced" ? "text-[var(--hc-muted)]" : longDominant ? "text-rose-400" : "text-emerald-400";
  const Icon = longDominant ? TrendingDown : TrendingUp;
  const total = Math.max(summary.total_usd, 1);

  return (
    <article className="min-w-0 rounded-lg border border-[var(--hc-border)] bg-[var(--hc-surface-1)] p-4" data-testid={`liquidation-card-${summary.window}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--hc-muted)]">{summary.window} liquidations</p>
          <p className="mt-3 text-2xl font-semibold tracking-[-0.03em] text-[var(--hc-text)]">{websiteUsd(summary.total_usd)}</p>
        </div>
        <span className={`rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] p-2 ${tone}`}>
          <Icon size={17} aria-hidden="true" />
        </span>
      </div>
      <div className="mt-4 flex h-1.5 overflow-hidden rounded-full bg-[var(--hc-bg)]" aria-hidden="true">
        <span className="bg-rose-500" style={{ width: `${Math.min(100, summary.long_usd / total * 100)}%` }} />
        <span className="bg-emerald-500" style={{ width: `${Math.min(100, summary.short_usd / total * 100)}%` }} />
      </div>
      <div className="mt-2 flex flex-col items-start gap-1 text-[11px] text-[var(--hc-muted)] min-[320px]:flex-row min-[320px]:items-center min-[320px]:justify-between min-[320px]:gap-2">
        <span>Long {websiteUsd(summary.long_usd)}</span>
        <span>Short {websiteUsd(summary.short_usd)}</span>
      </div>
      <p className={`mt-3 text-xs font-semibold ${tone}`}>{dominantLabel(summary)}</p>
    </article>
  );
}

function LiquidationHeatmapCanvas({ heatmap }: { heatmap: HistoricalLiquidationHeatmap }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const geometryRef = useRef({ left: 64, top: 12, width: 1, height: 1, xCount: 1, yCount: 1 });
  const [hover, setHover] = useState<{
    x: number;
    y: number;
    xIndex: number;
    yIndex: number;
    cell: HistoricalLiquidationHeatmap["cells"][number] | null;
    candle: HistoricalLiquidationHeatmap["candles"][number];
    price: number;
    strength: number;
  } | null>(null);
  const intensityScale = useMemo(() => {
    const values = heatmap.cells.map((cell) => cell.intensity).filter((value) => Number.isFinite(value) && value > 0).sort((a, b) => a - b);
    const at = (percentile: number) => values[Math.min(values.length - 1, Math.max(0, Math.floor((values.length - 1) * percentile)))] || 1;
    return { values, floor: at(0.08), ceiling: at(0.99) };
  }, [heatmap.cells]);
  const cellLookup = useMemo(() => new Map(heatmap.cells.map((cell) => [`${cell.x}:${cell.y}`, cell])), [heatmap.cells]);

  const intensityStrength = useCallback((value: number) => {
    const lower = Math.log1p(intensityScale.floor);
    const upper = Math.log1p(Math.max(intensityScale.ceiling, intensityScale.floor + 1));
    const normalized = Math.max(0, Math.min(1, (Math.log1p(Math.max(value, 0)) - lower) / Math.max(upper - lower, 0.001)));
    return Math.pow(normalized, 1.35);
  }, [intensityScale]);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;

    const draw = () => {
      const width = Math.max(280, Math.floor(host.getBoundingClientRect().width));
      const height = width < 520 ? 340 : width < 900 ? 440 : 520;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const context = canvas.getContext("2d");
      if (!context) return;
      context.scale(pixelRatio, pixelRatio);
      context.clearRect(0, 0, width, height);

      const margin = { top: 12, right: 12, bottom: 32, left: width < 420 ? 52 : 64 };
      const plotWidth = width - margin.left - margin.right;
      const plotHeight = height - margin.top - margin.bottom;
      context.fillStyle = "#07111f";
      context.fillRect(margin.left, margin.top, plotWidth, plotHeight);

      const xCount = Math.max(heatmap.candles.length, 1);
      const yCount = Math.max(heatmap.price_levels.length, 1);
      const cellWidth = Math.max(1, plotWidth / xCount + 0.35);
      const cellHeight = Math.max(1, plotHeight / yCount + 0.35);
      geometryRef.current = { left: margin.left, top: margin.top, width: plotWidth, height: plotHeight, xCount, yCount };

      for (const cell of heatmap.cells) {
        const strength = intensityStrength(cell.intensity);
        const [red, green, blue] = heatColor(strength);
        context.fillStyle = `rgba(${red},${green},${blue},${0.18 + strength * 0.82})`;
        const x = margin.left + (cell.x / xCount) * plotWidth;
        const y = margin.top + plotHeight - ((cell.y + 1) / yCount) * plotHeight;
        context.fillRect(x, y, cellWidth, cellHeight);
      }

      const priceRange = Math.max(heatmap.max_price - heatmap.min_price, 1);
      const priceY = (price: number) => margin.top + ((heatmap.max_price - price) / priceRange) * plotHeight;
      const candleSlotWidth = plotWidth / xCount;
      const candleBodyWidth = Math.max(1, Math.min(5.5, candleSlotWidth * 0.72));
      context.save();
      context.beginPath();
      context.rect(margin.left, margin.top, plotWidth, plotHeight);
      context.clip();
      heatmap.candles.forEach((candle, index) => {
        const x = margin.left + ((index + 0.5) / xCount) * plotWidth;
        const openY = priceY(candle.open);
        const highY = priceY(candle.high);
        const lowY = priceY(candle.low);
        const closeY = priceY(candle.close);
        const bullish = candle.close >= candle.open;
        const color = bullish ? "#2dd4bf" : "#fb7185";

        context.beginPath();
        context.moveTo(x, highY);
        context.lineTo(x, lowY);
        context.strokeStyle = color;
        context.globalAlpha = 0.92;
        context.lineWidth = Math.max(0.75, Math.min(1.2, candleBodyWidth * 0.34));
        context.stroke();

        const bodyTop = Math.min(openY, closeY);
        const bodyHeight = Math.max(1.4, Math.abs(closeY - openY));
        context.fillStyle = color;
        context.globalAlpha = 0.96;
        context.fillRect(x - candleBodyWidth / 2, bodyTop, candleBodyWidth, bodyHeight);
      });
      context.restore();

      const currentY = priceY(heatmap.current_price);
      context.setLineDash([5, 4]);
      context.beginPath();
      context.moveTo(margin.left, currentY);
      context.lineTo(margin.left + plotWidth, currentY);
      context.strokeStyle = "rgba(56,189,248,0.9)";
      context.lineWidth = 1;
      context.stroke();
      context.setLineDash([]);

      context.font = "11px ui-sans-serif, system-ui, sans-serif";
      context.fillStyle = "#94a3b8";
      context.textAlign = "right";
      for (const [price, y] of [[heatmap.max_price, margin.top + 4], [heatmap.current_price, currentY + 4], [heatmap.min_price, margin.top + plotHeight]]) {
        context.fillText(`$${Math.round(price).toLocaleString()}`, margin.left - 6, y);
      }
      const first = heatmap.candles.at(0);
      const middle = heatmap.candles.at(Math.floor(heatmap.candles.length / 2));
      const last = heatmap.candles.at(-1);
      const timeLabel = (timestamp?: number) => timestamp ? new Intl.DateTimeFormat("en", { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(timestamp * 1000)) : "";
      context.textAlign = "left";
      context.fillText(timeLabel(first?.time), margin.left, height - 9);
      context.textAlign = "center";
      context.fillText(timeLabel(middle?.time), margin.left + plotWidth / 2, height - 9);
      context.textAlign = "right";
      context.fillText(timeLabel(last?.time), margin.left + plotWidth, height - 9);

      if (hover) {
        context.setLineDash([3, 3]);
        context.strokeStyle = "rgba(226,232,240,0.48)";
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(hover.x, margin.top);
        context.lineTo(hover.x, margin.top + plotHeight);
        context.moveTo(margin.left, hover.y);
        context.lineTo(margin.left + plotWidth, hover.y);
        context.stroke();
        context.setLineDash([]);
      }
    };

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(host);
    return () => observer.disconnect();
  }, [heatmap, hover, intensityStrength]);

  const selectCell = (xIndex: number, yIndex: number, x: number, y: number) => {
    const geometry = geometryRef.current;
    const boundedX = Math.max(0, Math.min(geometry.xCount - 1, xIndex));
    const boundedY = Math.max(0, Math.min(geometry.yCount - 1, yIndex));
    const candle = heatmap.candles[boundedX];
    const price = heatmap.price_levels[boundedY];
    if (!candle || !Number.isFinite(price)) {
      setHover(null);
      return;
    }
    const cell = cellLookup.get(`${boundedX}:${boundedY}`) ?? null;
    setHover({ x, y, xIndex: boundedX, yIndex: boundedY, cell, candle, price, strength: cell ? intensityStrength(cell.intensity) : 0 });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const geometry = geometryRef.current;
    if (x < geometry.left || x > geometry.left + geometry.width || y < geometry.top || y > geometry.top + geometry.height) {
      setHover(null);
      return;
    }
    const xIndex = Math.floor(((x - geometry.left) / geometry.width) * geometry.xCount);
    const yIndex = Math.floor(((geometry.top + geometry.height - y) / geometry.height) * geometry.yCount);
    selectCell(xIndex, yIndex, x, y);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLCanvasElement>) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const geometry = geometryRef.current;
    const nextX = (hover?.xIndex ?? Math.floor(geometry.xCount / 2)) + (event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0);
    const nextY = (hover?.yIndex ?? Math.floor(geometry.yCount / 2)) + (event.key === "ArrowUp" ? 1 : event.key === "ArrowDown" ? -1 : 0);
    const boundedX = Math.max(0, Math.min(geometry.xCount - 1, nextX));
    const boundedY = Math.max(0, Math.min(geometry.yCount - 1, nextY));
    const x = geometry.left + ((boundedX + 0.5) / geometry.xCount) * geometry.width;
    const y = geometry.top + geometry.height - ((boundedY + 0.5) / geometry.yCount) * geometry.height;
    selectCell(boundedX, boundedY, x, y);
  };

  const strengthLabel = hover ? hover.strength >= 0.9 ? "Extreme" : hover.strength >= 0.68 ? "Strong" : hover.strength >= 0.38 ? "Building" : hover.cell ? "Weak" : "No modeled concentration" : "";

  return (
    <div ref={hostRef} className="relative min-w-0 overflow-hidden rounded-lg border border-slate-800/80 bg-[#050b14]">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`${heatmap.range} BTC liquidation heatmap with OHLC candles and current price at ${websiteUsd(heatmap.current_price)}`}
        className="block max-w-full touch-none cursor-crosshair focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-inset"
        data-testid="liquidation-heatmap"
        tabIndex={0}
        aria-describedby="liquidation-heatmap-help"
        onPointerMove={onPointerMove}
        onPointerDown={onPointerMove}
        onPointerLeave={() => setHover(null)}
        onKeyDown={onKeyDown}
      />
      <p id="liquidation-heatmap-help" className="sr-only">Move the pointer across the heatmap, tap a row, or use the arrow keys to inspect modeled liquidation amounts by time and price.</p>
      {hover ? (
        <div
          className="pointer-events-none absolute right-3 top-3 z-10 min-w-52 rounded-lg border border-slate-700 bg-slate-950/95 px-3.5 py-3 text-[11px] leading-5 text-slate-300 shadow-2xl"
          data-testid="liquidation-heatmap-tooltip"
          aria-live="polite"
        >
          <div className="flex items-center justify-between gap-4 border-b border-slate-800 pb-1.5">
            <p className="font-semibold text-slate-100">${hover.price.toLocaleString("en", { maximumFractionDigits: 0 })}</p>
            <p className="text-[10px] uppercase tracking-[0.08em] text-slate-400">{strengthLabel}</p>
          </div>
          <p className="mt-1.5 text-slate-400">{new Intl.DateTimeFormat("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" }).format(new Date(hover.candle.time * 1000))} UTC</p>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 rounded-md border border-slate-800 bg-slate-900/70 px-2.5 py-2 text-[10px] tabular-nums text-slate-200">
            <p><span className="text-slate-500">O </span>${hover.candle.open.toLocaleString("en", { maximumFractionDigits: 1 })}</p>
            <p><span className="text-slate-500">H </span>${hover.candle.high.toLocaleString("en", { maximumFractionDigits: 1 })}</p>
            <p><span className="text-slate-500">L </span>${hover.candle.low.toLocaleString("en", { maximumFractionDigits: 1 })}</p>
            <p><span className="text-slate-500">C </span>${hover.candle.close.toLocaleString("en", { maximumFractionDigits: 1 })}</p>
          </div>
          <p className="mt-1 text-slate-400">Estimated liquidations</p>
          <p className="text-lg font-semibold tabular-nums text-slate-50">{hover.cell ? websiteUsd(hover.cell.intensity) : "No modeled amount"}</p>
          {hover.cell ? <p className="mt-0.5 text-[10px] uppercase tracking-[0.08em] text-slate-400">Relative strength {Math.round(hover.strength * 100)}%</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function heatColor(strength: number): [number, number, number] {
  const stops: Array<[number, [number, number, number]]> = [
    [0, [2, 20, 70]],
    [0.4, [7, 63, 170]],
    [0.68, [0, 174, 209]],
    [0.84, [48, 202, 119]],
    [0.95, [218, 224, 34]],
    [1, [255, 126, 0]]
  ];
  const bounded = Math.max(0, Math.min(1, strength));
  const upperIndex = stops.findIndex(([position]) => position >= bounded);
  if (upperIndex <= 0) return stops[0][1];
  const [upperPosition, upper] = stops[upperIndex];
  const [lowerPosition, lower] = stops[upperIndex - 1];
  const mix = (bounded - lowerPosition) / Math.max(upperPosition - lowerPosition, 0.001);
  return lower.map((channel, index) => Math.round(channel + (upper[index] - channel) * mix)) as [number, number, number];
}

function ProjectedLiquidationTerminal({ heatmap, candles }: { heatmap: Extract<LiquidationHeatmap, { kind: "projected_snapshot" }>; candles: KlineBar[] }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const geometryRef = useRef({ left: 18, top: 30, width: 1, height: 1 });
  const [hover, setHover] = useState<{ x: number; y: number; candle: KlineBar; level?: { price: number; intensity: number } } | null>(null);
  const levels = useMemo(() => [...heatmap.levels].sort((left, right) => left.price - right.price), [heatmap.levels]);
  const bars = useMemo(
    () => candles.filter((bar) => [bar.time, bar.open, bar.high, bar.low, bar.close].every(Number.isFinite)).sort((left, right) => left.time - right.time).slice(-180),
    [candles]
  );

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas || !levels.length) return;

    const draw = () => {
      const width = Math.max(300, Math.floor(host.getBoundingClientRect().width));
      const height = width < 620 ? 400 : 560;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * pixelRatio);
      canvas.height = Math.floor(height * pixelRatio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.fillStyle = "#02040a";
      context.fillRect(0, 0, width, height);

      const margin = { top: 30, right: width < 620 ? 64 : 108, bottom: 34, left: width < 620 ? 8 : 18 };
      const plotWidth = Math.max(1, width - margin.left - margin.right);
      const plotHeight = Math.max(1, height - margin.top - margin.bottom);
      geometryRef.current = { left: margin.left, top: margin.top, width: plotWidth, height: plotHeight };
      const candleLow = bars.length ? Math.min(...bars.map((bar) => bar.low)) : heatmap.min_price;
      const candleHigh = bars.length ? Math.max(...bars.map((bar) => bar.high)) : heatmap.max_price;
      const rawMin = Math.min(heatmap.min_price, candleLow);
      const rawMax = Math.max(heatmap.max_price, candleHigh);
      const padding = Math.max((rawMax - rawMin) * 0.025, 100);
      const minPrice = rawMin - padding;
      const maxPrice = rawMax + padding;
      const priceRange = Math.max(maxPrice - minPrice, 1);
      const priceY = (price: number) => margin.top + ((maxPrice - price) / priceRange) * plotHeight;
      // CounterFlow's current snapshot values are often tightly clustered. A rank
      // scale preserves their ordering while reserving warm colors for only the
      // densest few zones.
      const levelsByIntensity = [...levels].sort((left, right) => left.intensity - right.intensity);
      const strengthByPrice = new Map(
        levelsByIntensity.map((level, index) => [
          level.price,
          Math.max(0.025, Math.pow(index / Math.max(levelsByIntensity.length - 1, 1), 2.35))
        ])
      );
      const normalizedIntensity = (price: number) => strengthByPrice.get(price) ?? 0.025;
      const prices = levels.map((level) => level.price);
      const priceSteps = prices.slice(1).map((price, index) => Math.abs(price - prices[index])).filter((step) => step > 0).sort((left, right) => left - right);
      const typicalStep = priceSteps.length
        ? Math.max(1, priceSteps[Math.floor(priceSteps.length / 2)])
        : Math.max(priceRange / 40, 1);
      const bandHeight = Math.max(2, Math.min(10, Math.abs(priceY(minPrice + typicalStep) - priceY(minPrice)) * 0.68));

      context.strokeStyle = "rgba(51,65,85,0.28)";
      context.lineWidth = 1;
      for (let index = 0; index <= 5; index += 1) {
        const x = margin.left + plotWidth * index / 5;
        const y = margin.top + plotHeight * index / 5;
        context.beginPath();
        context.moveTo(x, margin.top);
        context.lineTo(x, margin.top + plotHeight);
        context.stroke();
        context.beginPath();
        context.moveTo(margin.left, y);
        context.lineTo(margin.left + plotWidth, y);
        context.stroke();
      }

      // This is a current projection snapshot, not a historical heat matrix. Keep
      // the price history clean and let the captured field build toward the latest
      // candle instead of falsely backfilling solid bands across the whole month.
      const projectionStartX = margin.left + plotWidth * (width < 620 ? 0.48 : 0.4);
      const projectionWidth = margin.left + plotWidth - projectionStartX;
      const columnCount = width < 620 ? 24 : 54;
      const columnWidth = projectionWidth / columnCount;
      const sigma = typicalStep * 0.7;
      const smoothedStrengthAt = (price: number) => {
        let weightedStrength = 0;
        let weightTotal = 0;
        for (const level of levels) {
          const distance = (price - level.price) / Math.max(sigma, 1);
          const weight = Math.exp(-0.5 * distance * distance);
          weightedStrength += normalizedIntensity(level.price) * weight;
          weightTotal += weight;
        }
        return weightTotal > 0.025 ? weightedStrength / weightTotal : 0;
      };
      const rowCount = Math.max(60, Math.min(110, Math.round(plotHeight / 5)));
      const rowHeight = plotHeight / rowCount;
      for (let row = 0; row < rowCount; row += 1) {
        const price = maxPrice - priceRange * (row + 0.5) / rowCount;
        const strength = smoothedStrengthAt(price);
        if (strength < 0.012) continue;
        const [red, green, blue] = heatColor(strength);
        for (let column = 0; column < columnCount; column += 1) {
          const progress = (column + 1) / columnCount;
          const fade = Math.pow(progress, 1.65);
          const texture = 0.92 + 0.08 * Math.sin((row + 3) * 0.47 + column * 0.21);
          const alpha = Math.min(0.88, (0.07 + strength * 0.66) * fade * texture);
          context.fillStyle = `rgba(${red},${green},${blue},${alpha})`;
          context.fillRect(projectionStartX + column * columnWidth, margin.top + row * rowHeight, columnWidth + 0.6, rowHeight + 0.6);
        }
      }
      const projectionFade = context.createLinearGradient(projectionStartX, 0, projectionStartX + projectionWidth * 0.2, 0);
      projectionFade.addColorStop(0, "rgba(2,4,10,0.86)");
      projectionFade.addColorStop(1, "rgba(2,4,10,0)");
      context.fillStyle = projectionFade;
      context.fillRect(projectionStartX, margin.top, projectionWidth * 0.2, plotHeight);

      const volumeHeight = Math.min(72, plotHeight * 0.16);
      const maxVolume = Math.max(...bars.map((bar) => bar.volume ?? 0), 1);
      const candleStep = plotWidth / Math.max(bars.length, 1);
      const candleWidth = Math.max(1, Math.min(5, candleStep * 0.56));
      bars.forEach((bar, index) => {
        const x = margin.left + (index + 0.5) * candleStep;
        const rising = bar.close >= bar.open;
        const volume = bar.volume ?? 0;
        const volumeBarHeight = volume / maxVolume * volumeHeight;
        context.fillStyle = rising ? "rgba(45,212,191,0.2)" : "rgba(251,113,133,0.2)";
        context.fillRect(x - candleWidth / 2, margin.top + plotHeight - volumeBarHeight, candleWidth, volumeBarHeight);

        context.strokeStyle = rising ? "rgba(226,232,240,0.94)" : "rgba(251,113,133,0.94)";
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(x, priceY(bar.high));
        context.lineTo(x, priceY(bar.low));
        context.stroke();
        const bodyTop = priceY(Math.max(bar.open, bar.close));
        const bodyBottom = priceY(Math.min(bar.open, bar.close));
        context.fillStyle = rising ? "rgba(241,245,249,0.96)" : "rgba(244,63,94,0.94)";
        context.fillRect(x - candleWidth / 2, bodyTop, candleWidth, Math.max(1.5, bodyBottom - bodyTop));
      });

      const latestPrice = bars.at(-1)?.close;
      if (latestPrice) {
        const y = priceY(latestPrice);
        context.setLineDash([4, 4]);
        context.strokeStyle = "rgba(56,189,248,0.9)";
        context.beginPath();
        context.moveTo(margin.left, y);
        context.lineTo(margin.left + plotWidth, y);
        context.stroke();
        context.setLineDash([]);
        context.fillStyle = "#2563eb";
        context.fillRect(margin.left + plotWidth + 4, y - 10, margin.right - 8, 20);
        context.fillStyle = "#f8fafc";
        context.font = "600 10px ui-monospace, SFMono-Regular, monospace";
        context.textAlign = "left";
        context.fillText(`$${Math.round(latestPrice).toLocaleString()}`, margin.left + plotWidth + 8, y + 3.5);
      }

      for (const level of levels) {
        const strength = normalizedIntensity(level.price);
        const [red, green, blue] = heatColor(strength);
        const y = priceY(level.price);
        context.strokeStyle = `rgba(${red},${green},${blue},${0.35 + strength * 0.58})`;
        context.lineWidth = Math.max(1, bandHeight * 0.22);
        context.beginPath();
        context.moveTo(margin.left + plotWidth + 14, y);
        context.lineTo(margin.left + plotWidth + 14 + (margin.right - 24) * strength, y);
        context.stroke();
      }

      context.fillStyle = "#64748b";
      context.font = "10px ui-monospace, SFMono-Regular, monospace";
      context.textAlign = "right";
      for (let index = 0; index <= 4; index += 1) {
        const price = maxPrice - priceRange * index / 4;
        context.fillText(`$${Math.round(price).toLocaleString()}`, margin.left + plotWidth - 6, margin.top + plotHeight * index / 4 + 12);
      }
      context.textAlign = "left";
      const dateFormatter = new Intl.DateTimeFormat("en", { month: "short", day: "numeric", timeZone: "UTC" });
      [0, 0.25, 0.5, 0.75, 1].forEach((fraction, index) => {
        const candle = bars[Math.min(bars.length - 1, Math.round((bars.length - 1) * fraction))];
        if (!candle) return;
        const x = margin.left + plotWidth * fraction;
        context.textAlign = index === 0 ? "left" : index === 4 ? "right" : "center";
        context.fillText(dateFormatter.format(new Date(candle.time)), x, height - 10);
      });
      context.textAlign = "left";
      context.fillStyle = "#94a3b8";
      context.fillText("PRICE + VOLUME CONTEXT", margin.left, 14);
      context.textAlign = "center";
      context.fillStyle = "#64748b";
      context.fillText("CURRENT PROJECTION →", projectionStartX + projectionWidth * 0.55, 14);
      context.textAlign = "right";
      context.fillText("DENSITY PROFILE", width - 8, 14);
    };

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(host);
    return () => observer.disconnect();
  }, [bars, heatmap.max_price, heatmap.min_price, levels]);

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!bars.length) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const geometry = geometryRef.current;
    const fraction = Math.max(0, Math.min(1, (x - geometry.left) / geometry.width));
    const candle = bars[Math.min(bars.length - 1, Math.round((bars.length - 1) * fraction))];
    const priceFraction = Math.max(0, Math.min(1, (y - geometry.top) / geometry.height));
    const hoveredPrice = heatmap.max_price - (heatmap.max_price - heatmap.min_price) * priceFraction;
    const level = levels.reduce((closest, candidate) => Math.abs(candidate.price - hoveredPrice) < Math.abs(closest.price - hoveredPrice) ? candidate : closest, levels[0]);
    setHover({ x, y, candle, level });
  };

  return (
    <div ref={hostRef} className="relative min-w-0 overflow-hidden rounded-md border border-slate-800 bg-black">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`BitcoinCounterFlow projected liquidation density over ${bars.length} Binance four-hour BTC candles`}
        data-testid="projected-liquidation-terminal"
        className="block max-w-full touch-none"
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHover(null)}
      />
      {hover ? (
        <div
          className="pointer-events-none absolute z-10 min-w-40 rounded border border-slate-700 bg-slate-950/95 px-3 py-2 text-[10px] leading-5 text-slate-300 shadow-xl"
          style={{ left: Math.min(hover.x + 12, Math.max(8, (hostRef.current?.clientWidth ?? 240) - 180)), top: Math.max(8, hover.y - 60) }}
        >
          <p className="font-semibold text-slate-100">{new Date(hover.candle.time).toLocaleString("en", { timeZone: "UTC", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} UTC</p>
          <p>BTC close ${hover.candle.close.toLocaleString("en", { maximumFractionDigits: 0 })}</p>
          {hover.level ? <p>Cluster ${hover.level.price.toLocaleString("en", { maximumFractionDigits: 0 })} · {websiteUsd(hover.level.intensity)}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function LiquidationHeatmapView({ heatmap, candles }: { heatmap: LiquidationHeatmap; candles: KlineBar[] }) {
  if (heatmap.kind === "projected_snapshot") {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--hc-muted)]">
          <span>Browserbase liquidity bands</span>
          <span>Binance perpetual · 4h candles</span>
          <span className="flex items-center gap-1.5 normal-case tracking-normal"><span className="h-2 w-2 rounded-sm bg-blue-700" /> weaker</span>
          <span className="flex items-center gap-1.5 normal-case tracking-normal"><span className="h-2 w-2 rounded-sm bg-cyan-400" /> building</span>
          <span className="flex items-center gap-1.5 normal-case tracking-normal"><span className="h-2 w-2 rounded-sm bg-amber-400" /> dense</span>
        </div>
        <ProjectedLiquidationTerminal heatmap={heatmap} candles={candles} />
        <p className="max-w-4xl text-[11px] leading-5 text-[var(--hc-muted)]">Current CounterFlow projected liquidation bands over live Binance 4h price and volume context. Band strength is a snapshot and is not backfilled as historical liquidity.</p>
      </div>
    );
  }
  const maximum = Math.max(1, ...heatmap.levels.map((level) => level.intensity));
  const abovePrice = heatmap.levels.filter((level) => level.price >= heatmap.current_price).sort((left, right) => right.intensity - left.intensity).slice(0, 4);
  const belowPrice = heatmap.levels.filter((level) => level.price < heatmap.current_price).sort((left, right) => right.intensity - left.intensity).slice(0, 4);
  const strongestAbove = abovePrice[0];
  const strongestBelow = belowPrice[0];
  const levelRow = (level: HistoricalLiquidationHeatmap["levels"][number]) => (
    <div key={`${level.price}-${level.side}`} className="group relative overflow-hidden rounded-lg border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-3.5 py-3 transition-colors duration-200 hover:border-slate-600 hover:bg-slate-800/60">
      <span
        className={`absolute inset-y-0 left-0 opacity-[0.08] ${level.side === "longs" ? "bg-rose-400" : "bg-emerald-400"}`}
        style={{ width: `${Math.max(5, level.intensity / maximum * 100)}%` }}
        aria-hidden="true"
      />
      <div className="relative grid grid-cols-[1fr_auto] items-center gap-4">
        <div>
          <div className="flex items-baseline gap-2">
            <p className="text-sm font-semibold tabular-nums text-[var(--hc-text)]">${level.price.toLocaleString("en", { maximumFractionDigits: 0 })}</p>
            <span className="text-[10px] tabular-nums text-[var(--hc-muted)]">{level.distance_pct > 0 ? "+" : ""}{level.distance_pct.toFixed(2)}%</span>
          </div>
          <p className={`mt-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${level.side === "longs" ? "text-rose-400" : "text-emerald-400"}`}>{level.side} at risk</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold tabular-nums text-[var(--hc-text)]">{websiteUsd(level.intensity)}</p>
          <p className="mt-1 text-[9px] uppercase tracking-[0.1em] text-[var(--hc-muted)]">modeled total</p>
        </div>
      </div>
    </div>
  );
  return (
    <div className="min-w-0 space-y-4">
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="rounded-lg border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--hc-muted)]">BTC reference</p>
          <p className="mt-1.5 text-xl font-semibold tabular-nums text-[var(--hc-text)]">${heatmap.current_price.toLocaleString("en", { maximumFractionDigits: 0 })}</p>
          <p className="mt-1 text-[10px] text-[var(--hc-muted)]">CoinGlass matrix close</p>
        </div>
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-400">Strongest above price</p>
          <p className="mt-1.5 text-xl font-semibold tabular-nums text-[var(--hc-text)]">{strongestAbove ? `$${strongestAbove.price.toLocaleString("en", { maximumFractionDigits: 0 })}` : "—"}</p>
          <p className="mt-1 text-[10px] text-[var(--hc-muted)]">{strongestAbove ? `${websiteUsd(strongestAbove.intensity)} modeled · +${Math.abs(strongestAbove.distance_pct).toFixed(2)}%` : "No level available"}</p>
        </div>
        <div className="rounded-lg border border-rose-500/20 bg-rose-500/[0.06] px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-rose-400">Strongest below price</p>
          <p className="mt-1.5 text-xl font-semibold tabular-nums text-[var(--hc-text)]">{strongestBelow ? `$${strongestBelow.price.toLocaleString("en", { maximumFractionDigits: 0 })}` : "—"}</p>
          <p className="mt-1 text-[10px] text-[var(--hc-muted)]">{strongestBelow ? `${websiteUsd(strongestBelow.intensity)} modeled · −${Math.abs(strongestBelow.distance_pct).toFixed(2)}%` : "No level available"}</p>
        </div>
      </div>

      <div className="rounded-xl border border-[var(--hc-border)] bg-[var(--hc-surface-1)] p-3 sm:p-4">
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--hc-text)]">{HEATMAP_RANGE_TITLES[heatmap.range] ?? heatmap.range} liquidation concentration</h3>
            <p className="mt-1 text-[11px] text-[var(--hc-muted)]">Inspect by pointer, tap, or arrow keys. Values are CoinGlass modeled estimates.</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[10px] text-[var(--hc-muted)]" aria-label="Liquidation intensity scale">
            <span className="flex items-center gap-1.5"><span className="flex items-end gap-0.5" aria-hidden="true"><span className="h-2.5 w-1 bg-teal-400" /><span className="h-2 w-1 bg-rose-400" /></span> BTC OHLC</span>
            <span className="flex items-center gap-1.5"><span className="h-0.5 w-3 bg-sky-400" /> Current price</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-blue-950" /> Weak</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-blue-600" /> Building</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-cyan-400" /> Active</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-lime-400" /> Strong</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-orange-500" /> Extreme</span>
          </div>
        </div>
        <LiquidationHeatmapCanvas heatmap={heatmap} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section aria-labelledby="levels-above-price">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h3 id="levels-above-price" className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-400">Above price · shorts at risk</h3>
            <span className="text-[10px] text-[var(--hc-muted)]">Top modeled totals</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">{abovePrice.map(levelRow)}</div>
        </section>
        <section aria-labelledby="levels-below-price">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h3 id="levels-below-price" className="text-xs font-semibold uppercase tracking-[0.12em] text-rose-400">Below price · longs at risk</h3>
            <span className="text-[10px] text-[var(--hc-muted)]">Top modeled totals</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">{belowPrice.map(levelRow)}</div>
        </section>
      </div>
    </div>
  );
}

export function LiquidationDashboard({ initialData }: { initialData?: CoinGlassLiquidations | null }) {
  const [data, setData] = useState(initialData ?? null);
  const [candles, setCandles] = useState<KlineBar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<LiquidationPeriod>("24h");

  const loadData = useCallback(async (period: LiquidationPeriod) => {
    setLoading(true);
    setError(null);
    try {
      const selection = LIQUIDATION_PERIODS.find((option) => option.value === period) ?? LIQUIDATION_PERIODS[0];
      const [response, klineResponse] = await Promise.all([
        fetch(`/api/coinglass/liquidations?range=${selection.heatmapRange}&period=${period}`, {
          cache: "no-store",
          credentials: "same-origin"
        }),
        fetch(`/api/binance/klines?symbol=BTCUSDT&market=perp&interval=${selection.priceInterval}&limit=${selection.priceLimit}`, {
          cache: "no-store",
          credentials: "same-origin"
        })
      ]);
      if (!response.ok) throw new Error("CoinGlass liquidation totals are temporarily unavailable.");
      const payload = await response.json() as CoinGlassLiquidations;
      setData(payload);
      if (klineResponse.ok) {
        const klinePayload = await klineResponse.json() as KlineResponse;
        if (klinePayload.bars?.length) setCandles(klinePayload.bars);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "CoinGlass liquidation totals are temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData(selectedPeriod);
    const reload = () => void loadData(selectedPeriod);
    const timer = window.setInterval(reload, 60_000);
    window.addEventListener(DATA_REFRESHED_EVENT, reload);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(DATA_REFRESHED_EVENT, reload);
    };
  }, [selectedPeriod, loadData]);

  const summaries = useMemo(() => {
    const byWindow = new Map((data?.summaries ?? []).map((summary) => [summary.window, summary]));
    return (["1h", "4h", "12h", "24h"] as LiquidationSummary["window"][]).flatMap((window) => byWindow.get(window) ?? []);
  }, [data]);
  const selectedRange = LIQUIDATION_PERIODS.find((option) => option.value === selectedPeriod)?.heatmapRange ?? "24h";
  const activeHeatmap = data?.heatmap?.range === selectedRange ? data.heatmap : null;
  const projectedMap = activeHeatmap?.kind === "projected_snapshot";

  return (
    <section id="liquidations" aria-labelledby="liquidations-heading" className="min-w-0 max-w-full space-y-4 overflow-x-hidden">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--hc-muted)]">Leverage pressure</p>
          <h2 id="liquidations-heading" className="mt-1 text-xl font-semibold tracking-[-0.025em] text-[var(--hc-text)]">Total liquidations</h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-[var(--hc-muted)]">CoinGlass website-wide futures liquidations across all supported coins and exchanges.</p>
        </div>
        <button
          type="button"
          onClick={() => void loadData(selectedPeriod)}
          disabled={loading}
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[var(--hc-border-strong)] bg-[var(--hc-surface-1)] px-3 text-xs font-semibold text-[var(--hc-text)] transition-colors hover:bg-[var(--hc-surface-2)] disabled:opacity-60"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} aria-hidden="true" /> Refresh data
        </button>
      </div>

      {error ? <p role="alert" className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error} Showing the last validated totals when available.</p> : null}
      {data?.totals_cache_status === "stale" ? <p role="status" className="rounded-md border border-amber-400/40 bg-amber-400/5 px-3 py-2 text-xs leading-5 text-amber-200">CoinGlass liquidation totals are stale and shown only as the last validated snapshot. The price candles below continue to update from Binance.</p> : null}

      {summaries.length ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaries.map((summary) => <LiquidationCard key={summary.window} summary={summary} />)}
        </div>
      ) : (
        <Panel><EmptyState>Liquidation totals will appear after CoinGlass returns a validated response.</EmptyState></Panel>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-[var(--hc-muted)]">
        <span>CoinGlass · All coins · All exchanges</span>
        <span>{loading ? "Updating totals…" : data?.collected_at ? `Collected ${formatUtcTimestamp(data.collected_at)} UTC` : "Awaiting live response"}</span>
      </div>

      <Panel className="overflow-hidden">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-[var(--hc-text)]">{projectedMap ? "Projected liquidation map" : "Liquidation heatmap"}</h2>
            <p className="mt-1 max-w-3xl text-xs leading-5 text-[var(--hc-muted)]">
              {projectedMap
                ? `${data?.heatmap_source ?? "BitcoinCounterFlow"} · fixed one-month projected liquidity snapshot${data?.heatmap_collected_at ? ` · captured ${formatUtcTimestamp(data.heatmap_collected_at)} UTC` : ""}.`
                : `${data?.heatmap_source ?? "CoinGlass Model 2"} · historical estimated leverage concentrations.`}
            </p>
          </div>
          {projectedMap ? (
            <span className="rounded-md border border-[var(--hc-border)] px-3 py-2 text-[11px] font-semibold text-[var(--hc-muted)]">Fixed 1 month</span>
          ) : (
            <div className="grid w-full grid-cols-5 gap-1 rounded-lg border border-[var(--hc-border)] bg-[var(--hc-surface-2)] p-1 sm:flex sm:w-auto" role="group" aria-label="Liquidation heatmap period">
              {LIQUIDATION_PERIODS.map((period) => (
                <button
                  key={period.value}
                  type="button"
                  onClick={() => setSelectedPeriod(period.value)}
                  aria-label={`${period.label} liquidation heatmap period`}
                  aria-pressed={selectedPeriod === period.value}
                  className={`min-h-11 rounded-md px-3 text-[11px] font-semibold transition-colors sm:min-h-9 ${selectedPeriod === period.value ? "bg-[var(--hc-blue-soft)] text-[var(--hc-text)]" : "text-[var(--hc-muted)] hover:text-[var(--hc-text)]"}`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {activeHeatmap ? (
          <LiquidationHeatmapView heatmap={activeHeatmap} candles={candles} />
        ) : loading ? (
          <div className="h-[520px] animate-pulse rounded-lg bg-[var(--hc-surface-2)]" aria-label={`Loading ${selectedPeriod} liquidation chart`} />
        ) : (
          <div className="rounded-md border border-amber-400/25 bg-amber-400/5 px-4 py-5">
            <p className="text-sm font-semibold text-[var(--hc-text)]">Liquidation-map data is temporarily unavailable.</p>
            <p className="mt-1 max-w-3xl text-xs leading-5 text-[var(--hc-muted)]">The collector has not produced a validated CoinGlass heatmap matrix for this range yet.</p>
          </div>
        )}
      </Panel>
    </section>
  );
}
